import React, { useState, useMemo } from "react";
import { useCurrency } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { ChevronDown, Search, Check } from "lucide-react";

/** Compact currency picker for the B2C navbar. Shows the active code as a pill
 *  and pops a searchable list with the popular currencies sticky on top.
 *  Includes the effective rate (incl. margin) next to each row so power users
 *  immediately see "1 USD = X CCY" at the admin-managed rate. */
export default function CurrencyPicker() {
  const { currency, setCurrency, rates, rateMap, popular, loading } = useCurrency();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const current = rateMap[currency] || { code: currency, symbol: currency };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rates;
    return rates.filter(
      (r) => r.code.toLowerCase().includes(needle) || (r.name || "").toLowerCase().includes(needle)
    );
  }, [q, rates]);

  const popularList = useMemo(
    () => popular.map((c) => rateMap[c]).filter(Boolean),
    [popular, rateMap]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid="currency-picker-btn"
          variant="ghost"
          disabled={loading || rates.length === 0}
          className="rounded-full h-9 px-3 text-sm font-medium text-[var(--brand-accent)] hover:bg-teal-50"
        >
          <span className="font-mono">{current.symbol}</span>
          <span className="ml-1.5 font-mono text-xs text-slate-500">{current.code}</span>
          <ChevronDown className="w-3.5 h-3.5 ml-1 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="currency-picker-panel">
        <div className="p-3 border-b border-slate-200">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              data-testid="currency-picker-search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or code…"
              className="h-9 pl-7 text-sm"
            />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {!q && (
            <div className="px-3 pt-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5">Popular</div>
              {popularList.map((r) => (
                <CcyRow key={`pop-${r.code}`} r={r} active={currency === r.code}
                  onPick={() => { setCurrency(r.code); setOpen(false); }} />
              ))}
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mt-3 mb-1.5">All currencies</div>
            </div>
          )}
          <div className="px-3 pb-3">
            {filtered.map((r) => (
              <CcyRow key={r.code} r={r} active={currency === r.code}
                onPick={() => { setCurrency(r.code); setOpen(false); }} />
            ))}
            {filtered.length === 0 && (
              <div className="text-center text-xs text-slate-400 py-6">
                No currency matches “{q}”. <br />
                Ask the admin to add it on the FX page.
              </div>
            )}
          </div>
        </div>
        <div className="p-2.5 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500 leading-snug">
          Conversions use live Frankfurter + open.er-api rates plus the admin&apos;s FX margin. Charges always settle in USD.
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CcyRow({ r, active, onPick }) {
  const usdEq = r.usd_to_unit ? `1 USD = ${Number(r.usd_to_unit).toFixed(r.usd_to_unit < 1 ? 4 : 2)} ${r.code}` : "—";
  return (
    <button
      type="button"
      data-testid={`currency-row-${r.code}`}
      onClick={onPick}
      className={`w-full flex items-center justify-between gap-2 px-2 py-2 rounded-lg text-left text-sm hover:bg-teal-50 transition-colors ${active ? "bg-teal-50/70" : ""}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-mono font-bold text-[var(--brand-accent)] w-12 flex-shrink-0">{r.code}</span>
        <span className="text-slate-600 text-xs truncate">{r.name || r.code}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-slate-400 font-mono whitespace-nowrap hidden sm:inline">{usdEq}</span>
        {active && <Check className="w-3.5 h-3.5 text-[var(--brand-primary)]" />}
      </div>
    </button>
  );
}
